import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { KpiCard } from "@/components/Cards";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X, Clock, ArrowRight, Inbox, Layers, Timer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
    Submitted: "bg-gold-500/10 text-gold-700 border-gold-200",
    InProgress: "bg-blue-50 text-blue-700 border-blue-200",
    InForce: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Expired: "bg-navy-50 text-navy-500 border-navy-200",
    Declined: "bg-red-50 text-red-700 border-red-200",
};

export default function Applications() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();

    const load = () =>
        api.get("/broker/applications").then((r) => {
            setItems(r.data.items || []);
            setStats(r.data.stats || null);
        });

    useEffect(() => {
        load();
    }, []);

    const decide = async (e, id, application_status) => {
        e.stopPropagation();
        try {
            await api.patch(`/broker/applications/${id}`, { application_status });
            toast.success(`Application marked ${application_status}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Inbox"
                title="Member applications"
                description="Every calculator quote submitted by members. Click any row to open the full application, review coverage and update the policy status."
                testid="broker-applications-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                {stats && (
                    <div className="grid sm:grid-cols-4 gap-5">
                        <KpiCard label="Total" value={stats.total} icon={Layers} />
                        <KpiCard label="Submitted" value={stats.submitted} icon={Inbox} accent="pink" />
                        <KpiCard label="In progress" value={stats.in_progress} icon={Timer} />
                        <KpiCard label="Completed" value={stats.completed} icon={ShieldCheck} />
                    </div>
                )}

                {items.length === 0 ? (
                    <div className="rounded-2xl bg-white border border-navy-100/70 py-16 text-center">
                        <img
                            src="/panda/panda-virtual.png"
                            alt=""
                            className="h-32 mx-auto mb-4"
                        />
                        <div className="font-display text-2xl text-navy-700 mb-1">
                            Inbox zero, well done.
                        </div>
                        <div className="text-navy-500 text-sm">
                            No applications right now.
                        </div>
                    </div>
                ) : (
                    <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                        <Table data-testid="apps-table">
                            <TableHeader>
                                <TableRow className="bg-cream hover:bg-cream">
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Member</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Product</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Indicative premium</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Submitted</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Status</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em] text-right">Quick actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((a) => (
                                    <TableRow
                                        key={a.id}
                                        data-testid={`app-row-${a.id}`}
                                        onClick={() => navigate(`/broker/applications/${a.id}`)}
                                        className="cursor-pointer transition-colors hover:bg-cream/60 group"
                                    >
                                        <TableCell>
                                            <div className="text-navy-700 font-medium group-hover:text-gold-700 transition-colors inline-flex items-center gap-1.5">
                                                {a.member?.name || "Household member"}
                                                <ArrowRight
                                                    size={14}
                                                    className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                                />
                                            </div>
                                            <div className="text-xs text-navy-500">{a.member?.email || a.buyer_email}</div>
                                        </TableCell>
                                        <TableCell className="text-navy-700">{a.product_name}</TableCell>
                                        <TableCell className="text-sm">
                                            {a.calculator_quote != null ? (
                                                <span className="font-semibold text-gold-700">
                                                    {(a.currency || "hkd").toUpperCase()} {Number(a.calculator_quote).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            ) : (
                                                <span className="text-navy-500">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`${STATUS_STYLE[a.application_status] || ""} border`}>
                                                {a.application_status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="inline-flex gap-2">
                                                <Button
                                                    data-testid={`approve-${a.id}`}
                                                    onClick={(e) => decide(e, a.id, "InForce")}
                                                    size="sm"
                                                    disabled={a.application_status === "InForce"}
                                                    className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                                                >
                                                    <Check size={13} className="mr-1" /> In force
                                                </Button>
                                                <Button
                                                    data-testid={`hold-${a.id}`}
                                                    onClick={(e) => decide(e, a.id, "InProgress")}
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-8 rounded-full border-navy-100 text-navy-700 hover:bg-navy-50"
                                                >
                                                    <Clock size={13} className="mr-1" /> Hold
                                                </Button>
                                                <Button
                                                    data-testid={`reject-${a.id}`}
                                                    onClick={(e) => decide(e, a.id, "Declined")}
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-8 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                                                >
                                                    <X size={13} className="mr-1" /> Decline
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </>
    );
}
